// Deck Of The Week — fully automated:
//   - "current" is the highest-voted deck from the PREVIOUS ISO week.
//   - "candidates" are the top vote-getters in the CURRENT ISO week.
//   - Each logged-in user gets exactly one vote per ISO week.
// Displayed as a bold hero band on Home + a compact card in /decks index.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { t } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Trophy, Heart, Sparkle } from "@phosphor-icons/react";

export default function DeckOfWeekBanner() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();

  const load = () => api.get("/deck-of-week").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, [user?.id]);

  const vote = async (deckId) => {
    if (!user) { toast.error(t.dowLoginToVote); return; }
    if (data?.my_vote) { toast.error(t.dowAlreadyVoted); return; }
    setBusy(true);
    try {
      await api.post(`/decks/${deckId}/vote`);
      toast.success(t.dowVoteRecorded);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t.failed);
    } finally { setBusy(false); }
  };

  if (!data) return null;
  const showEmpty = !data.current && (data.candidates || []).length === 0;
  if (showEmpty) {
    // Iter 41 — empty state still fills the hero slot instead of
    // collapsing. Invites the first submission + vote.
    return (
      <section
        data-testid="deck-of-week-empty"
        className="border-y border-[#E2FF00]/20 bg-gradient-to-r from-[#E2FF00]/5 via-transparent to-[#FF0055]/5"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 grid md:grid-cols-[1fr_auto] gap-6 items-center">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Sparkle size={14} weight="fill" className="text-[#E2FF00]" />
              <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#E2FF00] font-mono">
                {t.dowEyebrow}
              </span>
            </div>
            <h2 className="font-display font-black uppercase italic text-3xl sm:text-4xl">
              {t.dowTitle}
            </h2>
            <p className="text-white/60 mt-2 text-sm font-mono max-w-2xl">
              {t.dowEmptyCta || t.dowSubtitle}
            </p>
          </div>
          <div className="flex gap-2 md:justify-end">
            <Link
              to="/deck-builder"
              data-testid="dow-empty-build"
              className="px-4 py-3 bg-[#E2FF00] text-black hover:bg-[#c7dc00] text-[10px] uppercase tracking-[0.25em] font-black"
            >
              {t.dowEmptyBuildBtn || t.deckBuilderTitle}
            </Link>
            <Link
              to="/decks"
              data-testid="dow-empty-browse"
              className="px-4 py-3 border border-white/20 text-white hover:border-white/40 text-[10px] uppercase tracking-[0.25em] font-bold"
            >
              {t.dowBrowseDecks}
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="deck-of-week-banner" className="border-y border-[#E2FF00]/20 bg-gradient-to-r from-[#E2FF00]/5 via-transparent to-[#FF0055]/5">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-center gap-2 mb-2">
          <Sparkle size={14} weight="fill" className="text-[#E2FF00]" />
          <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#E2FF00] font-mono">
            {t.dowEyebrow} · {data.week_key}
          </span>
        </div>
        <h2 className="font-display font-black uppercase italic text-3xl sm:text-4xl lg:text-5xl">
          {t.dowTitle}
        </h2>
        <p className="text-white/60 max-w-2xl mt-2 text-sm font-mono">{t.dowSubtitle}</p>

        <div className="grid lg:grid-cols-[1.5fr_1fr] gap-6 mt-8">
          {data.current ? (
            <FeaturedDeckCard deck={data.current} />
          ) : (
            <div className="border border-white/10 bg-[#0C0C14] p-6 text-white/50 font-mono text-sm">
              {t.dowNoWinnerYet}
            </div>
          )}
          <div className="space-y-3">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/50 font-mono">
              {t.dowNominees}
            </div>
            {(data.candidates || []).length === 0 && (
              <div className="text-white/40 text-sm font-mono">{t.dowNoNomineesYet}</div>
            )}
            {(data.candidates || []).map((d) => (
              <NomineeRow
                key={d.id}
                deck={d}
                voted={data.my_vote === d.id}
                canVote={!!user && !data.my_vote}
                onVote={() => vote(d.id)}
                busy={busy}
              />
            ))}
            {/* Fallback: encourage voting for any public deck if no candidates yet */}
            {(data.candidates || []).length === 0 && (
              <Link
                to="/decks"
                className="inline-block text-xs uppercase tracking-[0.25em] font-bold text-[#E2FF00] hover:underline"
              >
                {t.dowBrowseDecks} →
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function FeaturedDeckCard({ deck }) {
  return (
    <div data-testid={`dow-featured-${deck.id}`} className="relative border border-[#E2FF00]/40 bg-black/40 backdrop-blur overflow-hidden">
      <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-[#E2FF00] text-black px-2 py-1 text-[10px] uppercase tracking-[0.2em] font-black">
        <Trophy size={11} weight="fill" /> {t.dowWinnerBadge}
      </div>
      <div className="p-6 space-y-3">
        <div className="text-[10px] uppercase tracking-[0.25em] font-mono text-white/40">
          {deck.archetype} · {deck.beys?.length || 0} {t.beys}
        </div>
        <h3 className="font-display font-black italic uppercase text-3xl">{deck.name}</h3>
        {deck.description && (
          <p className="text-white/70 text-sm leading-relaxed line-clamp-3">{deck.description}</p>
        )}
        <div className="flex items-center gap-4 pt-3 border-t border-white/10">
          <div className="text-xs font-mono">
            <span className="text-white/40">{t.dowVotes}: </span>
            <span className="font-bold text-[#E2FF00]">{deck.dow_votes || 0}</span>
          </div>
          <div className="ml-auto flex gap-2">
            <Link
              to={`/decks/${deck.id}`}
              data-testid={`dow-view-${deck.id}`}
              className="px-3 py-2 border border-white/20 text-white hover:border-white/40 text-[10px] uppercase tracking-[0.2em] font-bold"
            >
              {t.dowView}
            </Link>
            <Link
              to={`/deck-builder/${deck.id}`}
              data-testid={`dow-load-${deck.id}`}
              className="px-3 py-2 bg-[#E2FF00] text-black hover:bg-[#c7dc00] text-[10px] uppercase tracking-[0.2em] font-bold"
            >
              {t.dowLoadIntoBuilder}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function NomineeRow({ deck, voted, canVote, onVote, busy }) {
  return (
    <div
      data-testid={`dow-nominee-${deck.id}`}
      className={`flex items-center gap-3 border p-3 transition-colors ${
        voted ? "border-[#FF0055]/60 bg-[#FF0055]/5" : "border-white/10 bg-[#0C0C14]/70"
      }`}
    >
      <div className="flex-1 min-w-0">
        <Link to={`/decks/${deck.id}`} className="font-display italic uppercase text-base truncate hover:text-[#00E5FF] block">
          {deck.name}
        </Link>
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40 truncate">
          {deck.archetype} · {deck.dow_votes || 0} {t.dowVotes}
        </div>
      </div>
      <button
        onClick={onVote}
        disabled={!canVote || voted || busy}
        data-testid={voted ? `dow-voted-${deck.id}` : `dow-vote-${deck.id}`}
        className={`px-3 py-2 border text-[10px] uppercase tracking-[0.2em] font-bold flex items-center gap-1.5 transition-colors ${
          voted
            ? "border-[#FF0055] bg-[#FF0055]/10 text-[#FF0055]"
            : "border-[#FF0055]/40 text-[#FF0055] hover:bg-[#FF0055]/10 disabled:opacity-40 disabled:cursor-not-allowed"
        }`}
      >
        <Heart size={11} weight={voted ? "fill" : "regular"} /> {voted ? t.dowVoted : t.dowVote}
      </button>
    </div>
  );
}
